import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { CategoryListParams, CategoryStatusFilter } from '@/types/category';

export interface CategoryQueryState {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search: string;
  status: CategoryStatusFilter;
  dateFrom: string;
  dateTo: string;
}

type ParamPatch = Partial<{
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search: string | null;
  status: CategoryStatusFilter | null;
  dateFrom: string | null;
  dateTo: string | null;
}>;

const DEFAULTS: CategoryQueryState = {
  page: 1,
  limit: 10,
  sortBy: 'sortOrder',
  sortOrder: 'asc',
  search: '',
  status: 'all',
  dateFrom: '',
  dateTo: '',
};

function parseStatus(value: string | null): CategoryStatusFilter {
  if (value === 'active' || value === 'inactive') return value;
  return 'all';
}

export function useCategoryQueryParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo<CategoryQueryState>(() => {
    const page = Number(searchParams.get('page') || DEFAULTS.page);
    const limit = Number(searchParams.get('limit') || DEFAULTS.limit);

    return {
      page: Number.isFinite(page) && page > 0 ? page : DEFAULTS.page,
      limit: Number.isFinite(limit) && limit > 0 ? Math.min(limit, 100) : DEFAULTS.limit,
      sortBy: searchParams.get('sortBy') || DEFAULTS.sortBy,
      sortOrder: searchParams.get('sortOrder') === 'desc' ? 'desc' : 'asc',
      search: searchParams.get('search') || '',
      status: parseStatus(searchParams.get('status')),
      dateFrom: searchParams.get('dateFrom') || '',
      dateTo: searchParams.get('dateTo') || '',
    };
  }, [searchParams]);

  const apiParams = useMemo<CategoryListParams>(
    () => ({
      page: state.page,
      limit: state.limit,
      sortBy: state.sortBy,
      sortOrder: state.sortOrder,
      ...(state.search ? { search: state.search } : {}),
      ...(state.status !== 'all' ? { status: state.status } : {}),
      ...(state.dateFrom ? { dateFrom: state.dateFrom } : {}),
      ...(state.dateTo ? { dateTo: state.dateTo } : {}),
    }),
    [state],
  );

  const patchParams = useCallback(
    (patch: ParamPatch, options?: { resetPage?: boolean }) => {
      setSearchParams(
        (searchParams) => {
          const next = new URLSearchParams(searchParams);
          const shouldResetPage = options?.resetPage ?? false;

          if (shouldResetPage) {
            next.set('page', '1');
          }

          const apply = (
            key: string,
            value: string | null | undefined,
            omitValues: string[] = [],
          ) => {
            if (value === null || value === undefined || value === '' || omitValues.includes(value)) {
              next.delete(key);
              return;
            }
            next.set(key, value);
          };

          if (patch.page !== undefined) next.set('page', String(patch.page));
          if (patch.limit !== undefined) next.set('limit', String(patch.limit));
          if (patch.sortBy !== undefined) {
            next.set('sortBy', patch.sortBy);
          }
          if (patch.sortOrder !== undefined) {
            next.set('sortOrder', patch.sortOrder);
          }
          if (patch.search !== undefined) apply('search', patch.search);
          if (patch.status !== undefined) apply('status', patch.status, ['all']);
          if (patch.dateFrom !== undefined) apply('dateFrom', patch.dateFrom);
          if (patch.dateTo !== undefined) apply('dateTo', patch.dateTo);

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setSearchParams(
      (searchParams) => {
        const next = new URLSearchParams(searchParams);
        next.delete('search');
        next.delete('status');
        next.delete('dateFrom');
        next.delete('dateTo');
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const resetAll = useCallback(() => {
    setSearchParams(
      (searchParams) => {
        const next = new URLSearchParams(searchParams);
        next.delete('search');
        next.delete('status');
        next.delete('dateFrom');
        next.delete('dateTo');
        next.delete('sortBy');
        next.delete('sortOrder');
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const hasActiveFiltersOrSort = useMemo(
    () =>
      state.search !== '' ||
      state.status !== 'all' ||
      state.dateFrom !== '' ||
      state.dateTo !== '' ||
      state.sortBy !== DEFAULTS.sortBy ||
      state.sortOrder !== DEFAULTS.sortOrder,
    [state],
  );

  return { state, apiParams, patchParams, clearFilters, resetAll, hasActiveFiltersOrSort };
}
